import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import useSWR from 'swr'
import { FormattedMessage, useIntl } from 'react-intl'
import _ from 'lodash'
import PT from 'prop-types'

import api from '~/services/api'
import MultiSelect from '~/components/MultiSelect'
import { useSelector } from 'react-redux'
import { Hide } from '~/components/commons'
import {
  ApplyDiv,
  ApplyButton,
  FilterCard,
  FilterPanelBody,
  FilterPanelItem,
} from './styles'
import { brazilianStates } from '~/constants/location'
import classNames from 'classnames'
import { CLIENT_ADMIN } from '~/constants/roles'

InsightsFilterPanel.propTypes = {
  filtersState: PT.object.isRequired,
  dispatch: PT.func.isRequired,
  onApply: PT.func.isRequired,
  showRating: PT.bool,
}

InsightsFilterPanel.defaultProps = {
  showRating: true,
}

function sortByProjectName(a, b) {
  var textA = a.label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
  var textB = b.label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
  return textA < textB ? -1 : textA > textB ? 1 : 0
}

function findLocationsFromOption(obj, result = []) {
  if (_.isArray(obj)) {
    return result.concat(obj)
  }

  return Object.values(obj).reduce((acc, value) => {
    if (_.isArray(value)) {
      return acc.concat(value)
    }

    return acc.concat(findLocationsFromOption(value))
  }, [])
}

const getFilteredLocationsNames = (filters, opts) => {
  const strippedFilteres = { ...filters }

  if (opts?.exclude) {
    opts.exclude.forEach(key => {
      delete strippedFilteres[key]
    })
  }

  const notEmptyFilters = _.pickBy(
    strippedFilteres,
    filter => _.isArray(filter) && !_.isEmpty(filter)
  )

  if (_.isEmpty(notEmptyFilters)) {
    return []
  }

  const result = _.intersection(...Object.values(notEmptyFilters))

  return _.uniq(result)
}

const hasSelectedFilters = filters => {
  const notEmptyFilters = _.pickBy(
    filters,
    filter => _.isArray(filter) && !_.isEmpty(filter)
  )

  return !_.isEmpty(notEmptyFilters)
}

function InsightsFilterPanel({
  filtersState: filters,
  dispatch,
  onApply: handleApply,
  noLabel = false,
  hiddenFilters,
}) {
  const intl = useIntl()
  const projectId = useSelector(state => state.user?.selectedProject?.id)
  const canImportProjects = useSelector(
    state => state.user?.profile?.account?.importProject
  )
  const userProfile = useSelector(state => state.user?.profile)
  const isClientAdmin = userProfile?.role === CLIENT_ADMIN

  const [selects, setSelects] = useState({})
  const [showAllOptions, setShowAllOptions] = useState(true)
  const [touchedFilter, setTouchedFilter] = useState('')
  const [listenSelectChange, setListenSelectChange] = useState(true)
  const filtersRef = useRef()
  const listenSelectChangeRef = useRef()
  const { data: locationsMeta } = useSWR(
    `/projects/${projectId}/locations/meta`,
    async url => {
      const response = await api.get(url)

      return response.data
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  )

  filtersRef.current = filters
  listenSelectChangeRef.current = listenSelectChange

  const allOptions = useMemo(() => {
    const result = {
      project: [],
      state: [],
      city: [],
      area: [],
      location: [],
      labels: [],
    }

    if (!locationsMeta) return result

    const { projects, addresses, labels, allLocations } = locationsMeta

    const stateArray = []

    const cityArray = []

    const areaArray = []

    Object.entries(addresses).forEach(([key, value]) => {
      brazilianStates.forEach(brazilianState => {
        if (key.toLowerCase() === brazilianState.uf.toLowerCase()) {
          stateArray.push({
            label: `${brazilianState.state} (${brazilianState.uf})`,
            value: findLocationsFromOption(value),
          })
        }
        if (key.toLowerCase() === brazilianState.state.toLowerCase()) {
          stateArray.push({
            label: `${brazilianState.state} (${brazilianState.uf})`,
            value: findLocationsFromOption(value),
          })
        }
      })
      Object.entries(value).forEach(([key, value]) => {
        cityArray.push({ label: key, value: findLocationsFromOption(value) })

        Object.entries(value).forEach(([key, value]) => {
          areaArray.push({
            label: key === 'undefined' ? 'No Address' : key,
            value: findLocationsFromOption(value),
          })
        })
      })
    })

    stateArray.forEach(item => {
      var existing = result.state.filter(v => {
        return v.label === item.label
      })
      if (existing.length) {
        var existingIndex = result.state.indexOf(existing[0])
        result.state[existingIndex].value = result.state[
          existingIndex
        ].value.concat(item.value)
      } else {
        result.state.push(item)
      }
    })

    cityArray.forEach(item => {
      var existing = result.city.filter(v => {
        return v.label === item.label
      })
      if (existing.length) {
        var existingIndex = result.city.indexOf(existing[0])
        result.city[existingIndex].value = result.city[
          existingIndex
        ].value.concat(item.value)
      } else {
        result.city.push(item)
      }
    })

    areaArray.forEach(item => {
      var existing = result.area.filter(v => {
        return v.label === 'No Address' && item.label === 'No Address'
      })
      if (existing.length) {
        var existingIndex = result.area.indexOf(existing[0])
        result.area[existingIndex].value = result.area[
          existingIndex
        ].value.concat(item.value)
      } else {
        result.area.push(item)
      }
    })

    result.state.sort(sortByProjectName)
    result.city.sort(sortByProjectName)

    result.location = allLocations?.map(location => {
      const title = location?.title || ''
      const address = location?.address || ''
      const storeCode = location?.storeCode || ''
      return {
        label: {
          ...location,
          text: `${title} ${address} ${storeCode}`,
        },
        value: [location.name],
      }
    })

    result.labels = Object.entries(labels).map(([key, value]) => ({
      label: key,
      value: value,
    }))

    result.project = Object.entries(projects).map(([key, value]) => ({
      label: key,
      value: value,
    }))

    return result
  }, [locationsMeta])

  const handleFilterChange = useCallback(
    _.debounce((options, filter) => {
      setListenSelectChange(true)

      const willClear = () => {
        const checkClear = { ...filters }
        delete checkClear[filter]

        return !hasSelectedFilters(checkClear)
      }

      const willClearAllFilters = willClear()
      const hasSlected = hasSelectedFilters(filters)

      if (filter === touchedFilter && !options.length) {
        setShowAllOptions(true)
      }

      if (!hasSlected) {
        setTouchedFilter(filter)
        setShowAllOptions(false)
      }

      if (!options?.length && willClearAllFilters) {
        setTouchedFilter('')
      }

      dispatch({
        type: '@filter/SET_PROPERTY',
        payload: {
          property: filter,
          value: _.flatten(options.map(option => option?.value)),
        },
      })
      setSelects(old => ({
        ...old,
        [filter]: options,
      }))
    }, 250),
    [filters]
  )

  const options = {
    project: useMemo(() => {
      if (!locationsMeta) return []

      const hasFilters = hasSelectedFilters(filters)
      if (!hasFilters) return []

      const filteredLocations = getFilteredLocationsNames(filters, {
        exclude: ['projects'],
      })

      const { projects } = locationsMeta
      const result = Object.entries(projects).reduce((acc, [key, value]) => {
        if (value?.some(name => filteredLocations.includes(name))) {
          acc.push({ label: key, value })
        }

        return acc
      }, [])

      result.sort(sortByProjectName)

      return result
    }, [filters, locationsMeta]),

    state: useMemo(() => {
      if (!locationsMeta) return []

      const hasFilters = hasSelectedFilters(filters)
      if (!hasFilters) return []

      const filteredLocations = getFilteredLocationsNames(filters, {
        exclude: ['state'],
      })

      const { addresses } = locationsMeta
      const result = Object.entries(addresses).reduce((acc, [key, value]) => {
        const optionsForEntry = findLocationsFromOption(value)

        if (optionsForEntry?.some(name => filteredLocations.includes(name))) {
          acc.push({ label: key, value: optionsForEntry })
        }

        return acc
      }, [])

      result.sort(sortByProjectName)

      return result
    }, [filters, locationsMeta]),

    city: useMemo(() => {
      if (!locationsMeta) return []

      const hasFilters = hasSelectedFilters(filters)
      if (!hasFilters) return []

      const filteredLocations = getFilteredLocationsNames(filters, {
        exclude: ['city'],
      })

      const { addresses } = locationsMeta
      const result = Object.values(addresses).reduce((acc, value) => {
        Object.entries(value).forEach(([key, value]) => {
          const optionsForEntry = findLocationsFromOption(value)

          if (optionsForEntry?.some(name => filteredLocations.includes(name))) {
            acc.push({ label: key, value: optionsForEntry })
          }
        })
        return acc
      }, [])

      result.sort(sortByProjectName)

      return result
    }, [filters, locationsMeta]),

    area: useMemo(() => {
      if (!locationsMeta) return []

      const hasFilters = hasSelectedFilters(filters)
      if (!hasFilters) return []

      const filteredLocations = getFilteredLocationsNames(filters, {
        exclude: ['locationArea'],
      })

      const result = []

      const { addresses } = locationsMeta
      const areaArray = Object.values(addresses).reduce((acc, value) => {
        Object.values(value).forEach(value => {
          Object.entries(value).forEach(([key, value]) => {
            const optionsForEntry = findLocationsFromOption(value)

            if (
              optionsForEntry?.some(name => filteredLocations.includes(name))
            ) {
              acc.push({
                label: key === 'undefined' ? 'No Address' : key,
                value: optionsForEntry,
              })
            }
          })
        })
        return acc
      }, [])

      areaArray.forEach(item => {
        var existing = result.filter(v => {
          return v.label === 'No Address' && item.label === 'No Address'
        })
        if (existing.length) {
          var existingIndex = result.indexOf(existing[0])
          result[existingIndex].value = result[existingIndex].value.concat(
            item.value
          )
        } else {
          result.push(item)
        }
      })

      result.sort(sortByProjectName)

      return result
    }, [filters, locationsMeta]),

    location: useMemo(() => {
      if (!locationsMeta) return []

      if (showAllOptions || touchedFilter === 'location')
        return allOptions?.location

      const hasFilters = hasSelectedFilters(filters)
      if (!hasFilters) return []

      const filteredLocations = getFilteredLocationsNames(filters, {
        exclude: ['location'],
      })

      const { allLocations } = locationsMeta

      const locationCustomOptions = []

      allLocations.forEach(location => {
        if (filteredLocations.includes(location.name)) {
          const title = location?.title || ''
          const address = location?.address || ''
          const storeCode = location?.storeCode || ''
          locationCustomOptions.push({
            label: {
              ...location,
              text: `${title} ${address} ${storeCode}`,
            },
            value: [location.name],
          })
        }
      })

      return locationCustomOptions
    }, [
      filters,
      locationsMeta,
      allOptions.location,
      showAllOptions,
      touchedFilter,
    ]),

    labels: useMemo(() => {
      if (!locationsMeta) return []

      const hasFilters = hasSelectedFilters(filters)
      if (!hasFilters) return []

      const filteredLocations = getFilteredLocationsNames(filters, {
        exclude: ['label'],
      })

      const { labels } = locationsMeta

      return Object.entries(labels).reduce((acc, [key, value]) => {
        if (value?.some(name => filteredLocations.includes(name))) {
          acc.push({ label: key, value })
        }

        return acc
      }, [])
    }, [filters, locationsMeta]),
  }

  useEffect(() => {
    if (!touchedFilter || !listenSelectChangeRef.current) return

    setListenSelectChange(false)
    setSelects(prev =>
      Object.entries(prev).reduce((acc, [key, options]) => {
        if (key === touchedFilter) {
          acc[key] = options
          return acc
        }

        acc[key] = []
        options.forEach(option => {
          if (
            filtersRef.current[touchedFilter].some(name =>
              option?.value.includes(name)
            )
          ) {
            acc[key].push(option)
          }
        })

        dispatch({
          type: '@filter/SET_PROPERTY',
          payload: {
            property: key,
            value: _.flatten(acc[key].map(option => option?.value)),
          },
        })
        return acc
      }, {})
    )
  }, [touchedFilter, selects, dispatch])

  useEffect(() => {
    setSelects([])
  }, [projectId])

  return (
    <FilterCard
      className={classNames('mb-1 d-flex align-items-center flex-row', {
        'display-hidden': hiddenFilters,
      })}
    >
      <FilterPanelBody>
        <div>
          {canImportProjects && isClientAdmin && (
            <FilterPanelItem>
              <MultiSelect
                placeholder={intl.formatMessage({ id: 'Project' })}
                uniqueKey="ProjectFilter"
                options={
                  (showAllOptions || touchedFilter === 'project'
                    ? allOptions
                    : options
                  )?.project
                }
                value={selects?.project}
                onChange={options => handleFilterChange(options, 'project')}
              />
            </FilterPanelItem>
          )}
          <FilterPanelItem>
            <MultiSelect
              placeholder={intl.formatMessage({ id: 'State' })}
              uniqueKey="StateFilter"
              options={
                (showAllOptions || touchedFilter === 'state'
                  ? allOptions
                  : options
                )?.state
              }
              value={selects?.state}
              onChange={options => handleFilterChange(options, 'state')}
            />
          </FilterPanelItem>

          <FilterPanelItem>
            <MultiSelect
              placeholder={intl.formatMessage({ id: 'City' })}
              uniqueKey="CityFilter"
              options={
                (showAllOptions || touchedFilter === 'city'
                  ? allOptions
                  : options
                )?.city
              }
              value={selects?.city}
              onChange={options => {
                handleFilterChange(options, 'city')
              }}
            />
          </FilterPanelItem>
        </div>
        <div>
          <FilterPanelItem>
            <MultiSelect
              uniqueKey="AreaFilter"
              placeholder={intl.formatMessage({ id: 'Location area' })}
              options={
                (showAllOptions || touchedFilter === 'locationArea'
                  ? allOptions
                  : options
                )?.area
              }
              value={selects?.locationArea}
              onChange={options => {
                handleFilterChange(options, 'locationArea')
              }}
            />
          </FilterPanelItem>
          <FilterPanelItem>
            <MultiSelect
              uniqueKey="LocationFilter"
              placeholder={intl.formatMessage({ id: 'Location' })}
              options={
                (showAllOptions || touchedFilter === 'location'
                  ? allOptions
                  : options
                )?.location
              }
              value={selects?.location}
              onChange={options => {
                handleFilterChange(options, 'location')
              }}
              renderOption={({ label: location }) => (
                <div className="col">
                  <span>{location?.title ?? ''}</span>
                  <small style={{ display: 'block', color: '#8D8D8D' }}>
                    {location?.address ?? ''}
                  </small>
                  <small style={{ display: 'block', color: '#8D8D8D' }}>
                    Store code: {location?.storeCode ?? ''}
                  </small>
                </div>
              )}
            />
          </FilterPanelItem>
          <Hide condition={noLabel}>
            <FilterPanelItem>
              <MultiSelect
                uniqueKey="LabelFilter"
                placeholder={intl.formatMessage({ id: 'Label' })}
                options={
                  (showAllOptions || touchedFilter === 'label'
                    ? allOptions
                    : options
                  )?.labels
                }
                value={selects?.label}
                onChange={options => {
                  handleFilterChange(options, 'label')
                }}
              />
            </FilterPanelItem>
          </Hide>
        </div>
      </FilterPanelBody>
      <ApplyDiv>
        <ApplyButton
          type="button"
          color={filters?.hasChanged ? 'primary' : 'secondary'}
          onClick={handleApply}
        >
          <FormattedMessage id="Apply" />
        </ApplyButton>
      </ApplyDiv>
    </FilterCard>
  )
}

export default InsightsFilterPanel
